import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import {
  CrmDashboard,
  CrmDashboardCustomerItem,
  CrmDashboardEventItem,
  CrmDashboardTaskItem,
  customerLink,
  dueBucketLabel,
  eventOrdersLink,
  formatDate,
  formatDateTime,
  formatMoney,
  relativeDueLabel,
  stageSharePercent,
} from '../../utils/crmDashboard';
import { priorityLabel } from '../../utils/crmPipeline';

interface Manager {
  id: string;
  name: string;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--crm-surface)',
  border: '1px solid var(--crm-border)',
  borderRadius: 10,
  padding: 16,
};

const sectionStyle: React.CSSProperties = { ...cardStyle, marginTop: 18 };

const stageBarColor = (stageCode: string): string => {
  switch (stageCode) {
    case 'won': return 'var(--crm-success)';
    case 'lost': return 'var(--crm-danger)';
    case 'proposal': return 'var(--crm-warning)';
    case 'qualified': return 'var(--crm-info)';
    case 'contacted': return 'var(--crm-accent)';
    default: return 'var(--crm-text-muted)';
  }
};

const KpiCard: React.FC<{ title: string; value: number | string; hint?: string; tone?: 'default' | 'warning' | 'danger' }> = ({ title, value, hint, tone = 'default' }) => {
  const color = tone === 'danger' ? 'var(--crm-danger)' : tone === 'warning' ? 'var(--crm-warning)' : 'var(--crm-text)';
  return (
    <div style={{ ...cardStyle, minWidth: 180, flex: '1 1 180px' }}>
      <div style={{ fontSize: 13, color: 'var(--crm-text-muted)' }}>{title}</div>
      <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{hint}</div>}
    </div>
  );
};

const EmptyRow: React.FC<{ text: string }> = ({ text }) => (
  <div style={{ padding: '10px 0', color: 'var(--crm-text-muted)' }}>{text}</div>
);

const TaskList: React.FC<{ items: CrmDashboardTaskItem[]; emptyText: string }> = ({ items, emptyText }) => {
  if (items.length === 0) return <EmptyRow text={emptyText} />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--crm-text-muted)' }}>
          <th style={{ padding: '6px 8px' }}>Завдання</th>
          <th style={{ padding: '6px 8px' }}>Клієнт</th>
          <th style={{ padding: '6px 8px' }}>Дедлайн</th>
          <th style={{ padding: '6px 8px' }}>Пріоритет</th>
          <th style={{ padding: '6px 8px' }}>Виконавець</th>
        </tr>
      </thead>
      <tbody>
        {items.map(task => (
          <tr key={task.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
            <td style={{ padding: '6px 8px' }}>{task.title}</td>
            <td style={{ padding: '6px 8px' }}>
              <Link to={customerLink(task.customerId)}>{task.customerName || `Клієнт #${task.customerId}`}</Link>
            </td>
            <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
              <div>{formatDateTime(task.dueAtUtc)}</div>
              <div style={{ fontSize: 12, color: task.dueBucket === 'overdue' ? 'var(--crm-danger)' : 'var(--crm-text-muted)' }}>
                {relativeDueLabel(task.dueAtUtc)} · {dueBucketLabel(task.dueBucket)}
              </div>
            </td>
            <td style={{ padding: '6px 8px' }}>{task.priorityName || priorityLabel(task.priorityCode)}</td>
            <td style={{ padding: '6px 8px' }}>{task.assignedUserName || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CustomerList: React.FC<{ items: CrmDashboardCustomerItem[]; emptyText: string; showNextContact: boolean }> = ({ items, emptyText, showNextContact }) => {
  if (items.length === 0) return <EmptyRow text={emptyText} />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--crm-text-muted)' }}>
          <th style={{ padding: '6px 8px' }}>Клієнт</th>
          <th style={{ padding: '6px 8px' }}>Стадія</th>
          {showNextContact && <th style={{ padding: '6px 8px' }}>Наступний контакт</th>}
          <th style={{ padding: '6px 8px' }}>Активні завдання</th>
          <th style={{ padding: '6px 8px' }}>Менеджер</th>
        </tr>
      </thead>
      <tbody>
        {items.map(customer => (
          <tr key={customer.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
            <td style={{ padding: '6px 8px' }}>
              <Link to={customerLink(customer.id)}>{customer.name || `Клієнт #${customer.id}`}</Link>
              <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{customer.contacts}</div>
            </td>
            <td style={{ padding: '6px 8px' }}>{customer.stageName}</td>
            {showNextContact && (
              <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                <div>{formatDateTime(customer.nextContactAtUtc)}</div>
                <div style={{ fontSize: 12, color: 'var(--crm-danger)' }}>{relativeDueLabel(customer.nextContactAtUtc)}</div>
              </td>
            )}
            <td style={{ padding: '6px 8px' }}>{customer.pendingTaskCount}</td>
            <td style={{ padding: '6px 8px' }}>{customer.assignedUserName || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const EventList: React.FC<{ items: CrmDashboardEventItem[] }> = ({ items }) => {
  if (items.length === 0) return <EmptyRow text="Найближчі 14 днів без запланованих подій." />;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--crm-text-muted)' }}>
          <th style={{ padding: '6px 8px' }}>Дата</th>
          <th style={{ padding: '6px 8px' }}>Подія</th>
          <th style={{ padding: '6px 8px' }}>Клієнт</th>
          <th style={{ padding: '6px 8px' }}>Гостей</th>
          <th style={{ padding: '6px 8px' }}>Статус</th>
          <th style={{ padding: '6px 8px' }}>Сума</th>
        </tr>
      </thead>
      <tbody>
        {items.map(order => (
          <tr key={order.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
            <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
              <div>{formatDate(order.eventDate)}</div>
              <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{relativeDueLabel(order.eventDate)}</div>
            </td>
            <td style={{ padding: '6px 8px' }}>
              <div>{order.eventName || `Замовлення #${order.id}`}</div>
              <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{order.eventVenue}</div>
              <Link to={eventOrdersLink()} style={{ fontSize: 12 }}>Відкрити список замовлень подій (№{order.id})</Link>
            </td>
            <td style={{ padding: '6px 8px' }}>
              {order.customerId
                ? <Link to={customerLink(order.customerId)}>{order.customerName || `Клієнт #${order.customerId}`}</Link>
                : (order.customerName || '—')}
            </td>
            <td style={{ padding: '6px 8px' }}>{order.guestCount}</td>
            <td style={{ padding: '6px 8px' }}>{order.orderStatus}</td>
            <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{formatMoney(order.totalPrice)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CrmDashboardPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<CrmDashboard | null>(null);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [managerFilter, setManagerFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get<CrmDashboard>('/crm-customers/dashboard', {
        params: { assignedUserId: managerFilter || undefined },
      });
      setDashboard(response.data);
    } catch (e: any) {
      setDashboard(null);
      setError(e?.response?.data?.message || 'Не вдалося завантажити операційну панель CRM.');
    } finally {
      setLoading(false);
    }
  }, [managerFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get<Manager[]>('/crm-customers/managers')
      .then(response => setManagers(response.data))
      .catch((e: any) => setError(e?.response?.data?.message || 'Не вдалося завантажити список менеджерів.'));
  }, []);

  const maxStageCount = dashboard ? Math.max(0, ...dashboard.pipeline.map(stage => stage.customerCount)) : 0;

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto', color: 'var(--crm-text)' }}>
      <h2 style={{ marginTop: 0 }}>Операційна панель CRM</h2>
      <p style={{ color: 'var(--crm-text-muted)' }}>
        Воронка продажів, прострочені контакти й завдання, найближчі події. Дані оновлюються при кожному завантаженні сторінки.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '16px 0' }}>
        <select
          value={managerFilter}
          onChange={event => setManagerFilter(event.target.value)}
          style={{ padding: '9px 11px', borderRadius: 6, border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
        >
          <option value="">Усі менеджери</option>
          {managers.map(manager => <option key={manager.id} value={manager.id}>{manager.name}</option>)}
        </select>
        <button type="button" onClick={load} style={{ padding: '9px 18px', border: '1px solid var(--crm-info)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-info-bg)', color: 'var(--crm-info)' }}>
          Оновити
        </button>
        <Link to="/management/customers" style={{ marginLeft: 'auto' }}>Перейти до CRM клієнтів →</Link>
      </div>

      {error && (
        <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: '10px 14px', borderRadius: 8, marginBottom: 14 }}>
          {error}
        </div>
      )}
      {loading && <div style={{ color: 'var(--crm-text-muted)' }}>Завантаження…</div>}

      {dashboard && (
        <>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <KpiCard title="Активні клієнти" value={dashboard.activeCustomerCount} hint={`Усього карток: ${dashboard.totalCustomerCount}`} />
            <KpiCard
              title="Конверсія закритих CRM-карток"
              value={dashboard.winRatePercent === null ? '—' : `${dashboard.winRatePercent.toLocaleString('uk-UA')}%`}
              hint={`${dashboard.wonCustomerCount} виграно з ${dashboard.closedCustomerCount} закритих`}
            />
            <KpiCard
              title="Повторні клієнти"
              value={dashboard.repeatCustomerCount}
              hint={`${dashboard.repeatCustomerRatePercent === null ? '—' : `${dashboard.repeatCustomerRatePercent.toLocaleString('uk-UA')}%`} серед ${dashboard.customersWithCompletedEventsCount} клієнтів із завершеними подіями`}
            />
            <KpiCard title="Прострочені контакти" value={dashboard.overdueContactCount} tone="danger" hint="Дата наступного контакту минула" />
            <KpiCard title="Без наступної дії" value={dashboard.customersWithoutNextActionCount} tone="warning" hint="Відкрита стадія, немає контакту й завдань" />
            <KpiCard title="Активні завдання" value={dashboard.pendingTaskCount} hint="Невиконані завдання" />
            <KpiCard title="Прострочені завдання" value={dashboard.overdueTaskCount} tone="danger" hint="Дедлайн минув" />
            <KpiCard title="Завдання на сьогодні" value={dashboard.tasksDueTodayCount} tone="warning" hint="Дедлайн до кінця доби (UTC)" />
            <KpiCard title="Завдання на 7 днів" value={dashboard.tasksDueNext7DaysCount} hint="Наступні 7 діб після сьогодні" />
            <KpiCard title="Події на 14 днів" value={dashboard.upcomingEventCount} hint="Без скасованих замовлень" />
          </div>

          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Воронка продажів</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dashboard.pipeline.map(stage => (
                <div key={stage.stageCode} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 200, fontSize: 14 }}>
                    {stage.stageName}
                    {!stage.isOpenStage && <span style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}> (закрита)</span>}
                  </div>
                  <div style={{ flex: 1, background: 'var(--crm-surface-alt)', borderRadius: 999, height: 14, overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${stageSharePercent(stage.customerCount, maxStageCount)}%`,
                        background: stageBarColor(stage.stageCode),
                        height: '100%',
                      }}
                      aria-hidden="true"
                    />
                  </div>
                  <div style={{ width: 48, textAlign: 'right', fontWeight: 600 }}>{stage.customerCount}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginTop: 10 }}>
              Ураховано лише активних клієнтів{dashboard.assignedUserName ? ` менеджера ${dashboard.assignedUserName}` : ''}.
            </div>
          </div>

          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Прострочені завдання ({dashboard.overdueTaskCount})</h3>
            <TaskList items={dashboard.overdueTasks} emptyText="Прострочених завдань немає." />
          </div>

          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Завдання: сьогодні та найближчі 7 днів ({dashboard.tasksDueTodayCount + dashboard.tasksDueNext7DaysCount})</h3>
            <TaskList items={dashboard.upcomingTasks} emptyText="Завдань із дедлайном на найближчий тиждень немає." />
          </div>

          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Прострочені контакти ({dashboard.overdueContactCount})</h3>
            <CustomerList items={dashboard.overdueContacts} emptyText="Усі заплановані контакти актуальні." showNextContact />
          </div>

          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Клієнти без наступної дії ({dashboard.customersWithoutNextActionCount})</h3>
            <CustomerList items={dashboard.customersWithoutNextAction} emptyText="Для всіх відкритих клієнтів заплановано наступний крок." showNextContact={false} />
          </div>

          <div style={sectionStyle}>
            <h3 style={{ marginTop: 0 }}>Найближчі події ({dashboard.upcomingEventCount})</h3>
            <EventList items={dashboard.upcomingEvents} />
          </div>

          <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginTop: 14 }}>
            Дані станом на {formatDateTime(dashboard.generatedAtUtc)}. Вікна розрахунку (UTC):
            сьогодні — {formatDateTime(dashboard.todayStartUtc)} … {formatDateTime(dashboard.todayEndUtc)};
            завдання — до {formatDateTime(dashboard.upcomingTasksEndUtc)};
            події — до {formatDateTime(dashboard.upcomingEventsEndUtc)}.
          </div>
        </>
      )}
    </div>
  );
};

export default CrmDashboardPage;